import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { addUserRoles, selectSetups } from "../../setupSlices";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import { Role } from "../../../auth/types/authType";
import {
  CheckBoxOutlineBlank,
  CheckCircleOutline,
  CheckCircleRounded,
  Save,
} from "@mui/icons-material";

interface Props {
  userId: string;
  /** Roles the acting admin is allowed to grant (their own role set). */
  assignableRoles: Role[];
}

/** Split PascalCase / camelCase identifiers into word tokens (e.g. `ViewCompanies` → View, Companies). */
function splitPascalCase(input: string): string[] {
  if (!input) return [];
  const spaced = input
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();
  return spaced.split(/\s+/).filter(Boolean);
}

export const UserRoles = ({ userId, assignableRoles }: Props) => {
  const { t } = useTranslation();
  const [privilegedRoles, setPrivilegedRoles] = useState<Role[]>([]);
  const { selectedUser, loading } = useAppSelector(selectSetups);
  const dispatch = useAppDispatch();

  /** `Module.Action` → the module segment (or the whole name if undotted). */
  const moduleOf = (displayName: string): string =>
    displayName.includes(".")
      ? displayName.slice(0, displayName.indexOf("."))
      : displayName;

  const formatWords = useMemo(
    () => (raw: string) =>
      splitPascalCase(raw)
        .map((part) =>
          t(`roleWords.${part}`, { defaultValue: part.replace(/_/g, " ") }),
        )
        .join(" "),
    [t],
  );

  /** Pretty label for a role toggle — the action segment after the dot. */
  const formatRoleLabel = useMemo(
    () => (displayName: string | undefined) => {
      if (!displayName) return "";
      const action = displayName.includes(".")
        ? displayName.slice(displayName.indexOf(".") + 1)
        : displayName;
      return formatWords(action) || action;
    },
    [formatWords],
  );

  /** Roles grouped by module, preserving first-seen module order. */
  const groupedRoles = useMemo(() => {
    const groups: { module: string; roles: Role[] }[] = [];
    const index = new Map<string, number>();
    for (const role of privilegedRoles) {
      const mod = moduleOf(role.displayName ?? "Other");
      if (!index.has(mod)) {
        index.set(mod, groups.length);
        groups.push({ module: mod, roles: [] });
      }
      groups[index.get(mod) as number].roles.push(role);
    }
    return groups;
  }, [privilegedRoles]);

  // useEffect(() => {
  //   //dispatch(changePageTitle(`Manage User`));
  //   if (userId.length > 0) {
  //     dispatch(getUser(userId as string));
  //   }
  // }, [dispatch, userId]); //user

  const toggleRole = (roleId: string) =>
    setPrivilegedRoles((prev) =>
      prev.map((role) =>
        role._id === roleId
          ? { ...role, isPrivileged: !role.isPrivileged }
          : role,
      ),
    );
  const CheckUnCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    let userRolesWithPriv = privilegedRoles.map((pr) => ({
      ...pr,
      isPrivileged: event.target.checked,
    }));
    setPrivilegedRoles(userRolesWithPriv);
  };

  useEffect(() => {
    if (selectedUser) {
      const userRoles = selectedUser?.roles as Role[];
      const userRolesWithPriv: Role[] = [];
      for (let index = 0; index < assignableRoles.length; index++) {
        const element = { ...assignableRoles[index] };
        if (userRoles && userRoles.find((rl) => element._id === rl._id)) {
          element.isPrivileged = true;
        } else {
          element.isPrivileged = false;
        }
        userRolesWithPriv.push(element);
      }
      setPrivilegedRoles(userRolesWithPriv);
    }
  }, [selectedUser, assignableRoles]);

  const SaveUserRoles = () => {
    const userRoles = privilegedRoles
      .filter((r) => r.isPrivileged)
      .map((rr) => rr._id);

    userRoles.unshift(userId);

    dispatch(addUserRoles(userRoles as string[]));
  };
  const selectedCount = privilegedRoles.filter((r) => r.isPrivileged).length;
  const allSelected =
    privilegedRoles.length > 0 && selectedCount === privilegedRoles.length;

  return (
    <Box
      sx={{ width: "100%", maxWidth: 880, mx: "auto", px: { xs: 1, sm: 2 } }}
    >
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          mb: 3,
          borderRadius: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              color="secondary"
              name="isAllPrivileged"
              checked={allSelected}
              onChange={CheckUnCheckAll}
            />
          }
          label="Select All"
        />
        <Typography variant="body2" color="text.secondary">
          {selectedCount} selected
        </Typography>
      </Paper>

      {loading === "pending" ? (
        <Stack spacing={2.5}>
          {new Array(4).fill(0).map((_, gi) => (
            <Box key={gi}>
              <Skeleton width={120} height={22} sx={{ mb: 1 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {new Array(6).fill(0).map((__, i) => (
                  <Skeleton key={i} variant="rounded" width={96} height={32} />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {groupedRoles.map((group) => (
            <Box key={group.module}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 1.25 }}
              >
                <Typography
                  variant="subtitle2"
                  title={group.module}
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatWords(group.module) || group.module}
                </Typography>
                <Box sx={{ flexGrow: 1, height: "1px", bgcolor: "divider" }} />
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {group.roles.map((role) => (
                  <Chip
                    key={role._id}
                    title={role.displayName}
                    label={formatRoleLabel(role.displayName)}
                    onClick={() => toggleRole(role._id as string)}
                    color={role.isPrivileged ? "primary" : "default"}
                    variant="outlined"
                    icon={
                      role.isPrivileged ? (
                        <CheckCircleRounded
                          fontSize="small"
                          color="secondary"
                        />
                      ) : (
                        <CheckBoxOutlineBlank
                          fontSize="small"
                          color="primary"
                        />
                      )
                    }
                    sx={{
                      width: 180,
                      borderRadius: 1.5,
                      fontWeight: 500,
                      fontSize: 14,
                      justifyContent: "flex-start",
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "transparent" },
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Box pb={3} mt={4}>
        <Button
          type="submit"
          startIcon={<Save />}
          sx={{ width: "100%", py: 1.25, borderRadius: 2 }}
          color="secondary"
          variant="contained"
          onClick={SaveUserRoles}
        >
          Assign Selected Roles to {selectedUser?.name}
        </Button>
      </Box>
    </Box>
  );
};
